import { useState, useMemo, useEffect, useRef, Fragment } from 'react';
import { useMetaData, useApi } from '../hooks/useApi';
import { useMonth } from '../context/MonthContext';
import { Football } from '../utils/api';
import { BarChart } from '../components/Charts';
import { COLORS, PALETTE, fmtNum, fmtPct } from '../utils/theme';

const ALL_UNIVERSE = 'ALL Universe';

// Football group display order (groups not listed fall to the end, alphabetically).
const GROUP_ORDER = [
  'All Football',
  'Football Club',
  'General Sports',
  'Score Apps',
  'Premier League Fantasy',
];

function fmtM(n) {
  if (n == null) return '—';
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return Number(n).toFixed(0);
}

// Affinity index → background colour (red < 100, green > 100, graded by distance)
function indexColor(idx) {
  if (idx == null || !isFinite(idx)) return 'transparent';
  const diff = idx - 100;
  const a = Math.min(Math.abs(diff) / 80, 1) * 0.5;
  return diff >= 0 ? `rgba(29,184,154,${a.toFixed(3)})` : `rgba(229,72,77,${a.toFixed(3)})`;
}

// ── App picker: searchable checkbox dropdown grouped by micro-category ──────────
function AppPicker({ apps, byMicro, selected, onToggle, onSetMicro }) {
  const [open,   setOpen]   = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const q = search.trim().toLowerCase();
  const micros = useMemo(
    () => Object.keys(byMicro).sort((a, b) => a.localeCompare(b)),
    [byMicro],
  );

  return (
    <div className="fa-picker" ref={ref}>
      <button className="filter-select fa-picker-trigger" onClick={() => setOpen(o => !o)}>
        {selected.size} app{selected.size === 1 ? '' : 's'} selected ▾
      </button>
      {open && (
        <div className="fa-picker-pop">
          <input
            className="filter-select fa-picker-search"
            placeholder="Search apps…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
          <div className="fa-picker-list">
            {micros.map(micro => {
              const list = byMicro[micro].filter(a => !q || a.toLowerCase().includes(q));
              if (!list.length) return null;
              return (
                <div key={micro} className="fa-picker-group">
                  <div className="fa-picker-group-head">
                    <span>{micro}</span>
                    <button className="fa-picker-addall" onClick={() => onSetMicro(micro)}>
                      add all
                    </button>
                  </div>
                  {list.map(a => (
                    <label key={a} className="fa-picker-item">
                      <input
                        type="checkbox"
                        checked={selected.has(a)}
                        onChange={() => onToggle(a)}
                      />
                      <span>{a}</span>
                    </label>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function FootballAffinityView() {
  const { month }                          = useMonth();
  const { latestMonth, loading: metaLoading } = useMetaData();
  const selectedMonth = month || latestMonth;

  const [selectedApps, setSelectedApps] = useState(() => new Set());
  const [metric,         setMetric]     = useState('pct');   // 'pct' | 'users'
  const [expanded,       setExpanded]   = useState(() => new Set());  // expanded football groups
  const [entitySearch,   setEntitySearch] = useState('');
  const [sort,           setSort]        = useState({ app: null, dir: 'desc' }); // column sort
  const [activeFootball, setActiveFootball] = useState('Any Football');
  const [compareSel,     setCompareSel]  = useState(() => new Set());  // apps to compare
  const initialisedRef = useRef(false);

  // App catalogue (drives micro-category picker + checkbox list)
  const { data: appCatalog, loading: catLoading } = useApi(() => Football.apps(), {}, true);

  const byMicro = useMemo(() => {
    const map = {};
    (appCatalog || []).forEach(r => {
      const micro = r.micro_category || 'Other / Universe';
      (map[micro] = map[micro] || []).push(r.application);
    });
    Object.values(map).forEach(list => list.sort((a, b) => a.localeCompare(b)));
    return map;
  }, [appCatalog]);

  // Default column set: ALL Universe + a mid-sized micro-category (~12 apps) on first load
  useEffect(() => {
    if (initialisedRef.current || !appCatalog?.length) return;
    const micros = Object.keys(byMicro).filter(m => m !== 'Other / Universe');
    if (!micros.length) return;
    const best = micros.reduce((a, b) =>
      Math.abs(byMicro[b].length - 12) < Math.abs(byMicro[a].length - 12) ? b : a,
    );
    setSelectedApps(new Set(byMicro[best]));
    initialisedRef.current = true;
  }, [appCatalog, byMicro]);

  const appsArray = useMemo(() => Array.from(selectedApps), [selectedApps]);

  // Always fetch ALL Universe too (needed for KPIs even if not shown as a column)
  const fetchApps = useMemo(
    () => Array.from(new Set([...appsArray, ALL_UNIVERSE])),
    [appsArray],
  );

  const { data, loading, error } = useApi(
    () => Football.pivot(selectedMonth, fetchApps),
    { selectedMonth, fetchApps },
    !!selectedMonth && fetchApps.length > 0,
  );

  // ── Pivot shaping ─────────────────────────────────────────────────────────────
  const { footballRows, groups, columns, kpis } = useMemo(() => {
    if (!data?.length) return { footballRows: [], groups: [], columns: [], kpis: {} };

    // Column ordering: by 'Any Football' overlap desc (ALL Universe naturally first)
    const anyFootballXy = {};
    data.forEach(r => {
      if (r.football_y === 'Any Football') anyFootballXy[r.application] = r.active_users_xy || 0;
    });
    const columns = appsArray
      .slice()
      .sort((a, b) => (anyFootballXy[b] || 0) - (anyFootballXy[a] || 0));

    // Group football entities → rows
    const rowMap = new Map();
    data.forEach(r => {
      const key = r.football_y;
      if (!rowMap.has(key)) {
        rowMap.set(key, {
          football_y:     r.football_y,
          football_group: r.football_group,
          focus_type:     r.focus_type,
          active_users_y: r.active_users_y,
          cells:          {},
          idx:            {},
        });
      }
      rowMap.get(key).cells[r.application] = r.active_users_xy || 0;
      rowMap.get(key).idx[r.application] = r.affinity_index;
    });

    // Sort: by group order, then group-subtotal rows before brand rows, then name
    const rows = Array.from(rowMap.values()).sort((a, b) => {
      const ga = GROUP_ORDER.indexOf(a.football_group);
      const gb = GROUP_ORDER.indexOf(b.football_group);
      const oa = ga === -1 ? 999 : ga;
      const ob = gb === -1 ? 999 : gb;
      if (oa !== ob) return oa - ob;
      if (a.focus_type !== b.focus_type) return a.focus_type === 'group' ? -1 : 1;
      return a.football_y.localeCompare(b.football_y);
    });

    // Row total across the DISPLAYED columns → % of row total
    rows.forEach(row => {
      row.rowTotal = columns.reduce((s, app) => s + (row.cells[app] || 0), 0);
    });

    // Nest into major entities (group rows) → sub-entities (brand rows)
    const groupMap = new Map();
    const groups = [];
    rows.forEach(row => {
      if (!groupMap.has(row.football_group)) {
        const g = { group: row.football_group, groupRow: null, brands: [] };
        groupMap.set(row.football_group, g);
        groups.push(g);
      }
      const g = groupMap.get(row.football_group);
      if (row.focus_type === 'group') g.groupRow = row;
      else g.brands.push(row);
    });
    // Fallback: if a group has no explicit 'group' subtotal row, promote its first brand
    groups.forEach(g => {
      if (!g.groupRow && g.brands.length) g.groupRow = g.brands.shift();
    });

    const allMobile     = data.find(r => r.application === ALL_UNIVERSE)?.active_users_x ?? null;
    const footballUsers = data.find(r => r.football_y === 'Any Football')?.active_users_y ?? null;

    return {
      footballRows: rows,
      groups,
      columns,
      kpis: {
        allMobile,
        footballUsers,
        penetration: allMobile && footballUsers ? (footballUsers / allMobile) * 100 : null,
      },
    };
  }, [data, appsArray]);

  // Market-share chart for the active football entity across selected apps
  const chart = useMemo(() => {
    const row = footballRows.find(r => r.football_y === activeFootball) || footballRows[0];
    if (!row) return null;
    const plot = app => {
      const v = row.cells[app] || 0;
      if (metric === 'pct')   return row.rowTotal ? (v / row.rowTotal) * 100 : 0;
      if (metric === 'index') return row.idx?.[app] ?? 0;
      return v;
    };
    // Re-sort descending by the selected entity's value every time
    const items = columns
      .filter(app => app !== ALL_UNIVERSE)
      .map(app => ({ app, val: plot(app) }))
      .filter(x => x.val > 0)
      .sort((a, b) => b.val - a.val);
    return {
      title:      row.football_y,
      group:      row.football_group,
      isSub:      row.focus_type !== 'group',
      categories: items.map(x => x.app),
      values:     items.map(x => x.val),
    };
  }, [footballRows, columns, activeFootball, metric]);

  const toggleApp = app => setSelectedApps(prev => {
    const next = new Set(prev);
    if (next.has(app)) next.delete(app); else next.add(app);
    return next;
  });

  const setMicro = micro => setSelectedApps(prev => {
    const next = new Set(prev);
    (byMicro[micro] || []).forEach(a => next.add(a));
    return next;
  });

  const onPickMicroSelect = micro => {
    if (!micro) return;
    setSelectedApps(new Set(byMicro[micro] || []));
  };

  // Independent toggle: opening a major entity leaves already-open ones expanded
  const toggleExpand = group => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(group)) next.delete(group); else next.add(group);
    return next;
  });

  if (metaLoading || catLoading) {
    return <div className="loading-state"><div className="spinner" /> Loading…</div>;
  }

  const micros = Object.keys(byMicro)
    .filter(m => m !== 'Other / Universe')
    .sort((a, b) => a.localeCompare(b));

  const fmtCell = (row, app) => {
    if (metric === 'users') { const v = row.cells[app] || 0; return v ? fmtM(v) : '—'; }
    if (metric === 'index') { const v = row.idx?.[app]; return v != null ? Math.round(v) : '—'; }
    const pct = row.rowTotal ? ((row.cells[app] || 0) / row.rowTotal) * 100 : 0;
    return pct ? pct.toFixed(2) + '%' : '—';
  };
  // Heatmap: same scheme as the Media Budget tables — red↔green index shading
  const cellStyle = (row, app) => metric === 'index' ? { background: indexColor(row.idx?.[app]) } : undefined;

  // Filter football entities by search. A group shows if its major entity matches
  // (all sub-entities visible) or any sub-entity matches (only those, auto-expanded).
  // Click a column header to sort rows by that app (toggles desc → asc)
  const onSortClick = app => setSort(s =>
    s.app === app ? { app, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { app, dir: 'desc' });

  const sortVal = row => {
    if (metric === 'users') return row.cells[sort.app] || 0;
    if (metric === 'index') return row.idx?.[sort.app] ?? -Infinity;
    const v = row.cells[sort.app] || 0;
    return row.rowTotal ? v / row.rowTotal : 0;
  };
  const sortCmp = (a, b) => sort.dir === 'desc' ? sortVal(b) - sortVal(a) : sortVal(a) - sortVal(b);

  const eq = entitySearch.trim().toLowerCase();
  let visibleGroups = groups
    .map(g => {
      if (!g.groupRow) return null;
      if (!eq) return { ...g, forceOpen: false };
      const majorMatch = g.groupRow.football_y.toLowerCase().includes(eq);
      if (majorMatch) return { ...g, forceOpen: true };
      const brands = g.brands.filter(b => b.football_y.toLowerCase().includes(eq));
      return brands.length ? { ...g, brands, forceOpen: true } : null;
    })
    .filter(Boolean);

  // Column sort: order sub-entities within each group, and the groups themselves —
  // but keep the 'All Football' (Any Football) aggregate pinned at the top.
  if (sort.app) {
    visibleGroups = visibleGroups.map(g => ({ ...g, brands: [...g.brands].sort(sortCmp) }));
    const pinned = visibleGroups.filter(g => g.group === 'All Football');
    const rest   = visibleGroups.filter(g => g.group !== 'All Football').sort((a, b) => sortCmp(a.groupRow, b.groupRow));
    visibleGroups = [...pinned, ...rest];
  }

  // ── Custom "Compare apps" section ──────────────────────────────────────────
  const compareAppList = columns.filter(a => a !== ALL_UNIVERSE);
  const chosenCompare = [...compareSel].filter(a => compareAppList.includes(a));
  const compareApps = chosenCompare.length ? chosenCompare : compareAppList.slice(0, 3);
  const compareEntities = visibleGroups.map(g => g.groupRow).filter(Boolean);
  const cmpVal = (row, app) => {
    if (metric === 'users') return row.cells[app] || 0;
    if (metric === 'index') return row.idx?.[app] ?? 0;
    const v = row.cells[app] || 0;
    return row.rowTotal ? (v / row.rowTotal) * 100 : 0;
  };
  const toggleCompare = app => setCompareSel(prev => {
    const next = new Set(prev.size ? prev : compareApps);
    if (next.has(app)) next.delete(app); else next.add(app);
    return next;
  });

  return (
    <div>
      {/* ── Controls ─────────────────────────────────────────────── */}
      <div className="filters-bar">
        <span className="filter-label">Micro-category</span>
        <select
          className="filter-select"
          defaultValue=""
          onChange={e => onPickMicroSelect(e.target.value)}
        >
          <option value="">Choose to load its apps…</option>
          {micros.map(m => (
            <option key={m} value={m}>{m} ({byMicro[m].length})</option>
          ))}
        </select>

        <span className="filter-label">Apps</span>
        <AppPicker
          apps={appCatalog || []}
          byMicro={byMicro}
          selected={selectedApps}
          onToggle={toggleApp}
          onSetMicro={setMicro}
        />

        <div style={{ flex: 1 }} />

        <div className="dj-metric-toggle">
          <button
            className={`dj-metric-btn ${metric === 'pct' ? 'active' : ''}`}
            onClick={() => setMetric('pct')}
          >% of Row</button>
          <button
            className={`dj-metric-btn ${metric === 'users' ? 'active' : ''}`}
            onClick={() => setMetric('users')}
          >Users</button>
          <button
            className={`dj-metric-btn ${metric === 'index' ? 'active' : ''}`}
            onClick={() => setMetric('index')}
          >Index vs National</button>
        </div>
      </div>

      {/* ── KPI header (Panel Size style) ────────────────────────── */}
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <div className="kpi-card" style={{ background: COLORS.teal, borderColor: COLORS.teal }}>
          <div className="kpi-label" style={{ color: 'rgba(255,255,255,0.85)' }}>All Mobile Users</div>
          <div className="kpi-value" style={{ color: '#fff' }}>{fmtNum(kpis.allMobile)}</div>
          <div className="kpi-delta neutral" style={{ color: 'rgba(255,255,255,0.75)' }}>
            ALL Universe · all mobile phone users
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Football Users</div>
          <div className="kpi-value">{fmtNum(kpis.footballUsers)}</div>
          <div className="kpi-delta neutral">'Any Football' deduplicated</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Football Penetration</div>
          <div className="kpi-value">{kpis.penetration != null ? fmtPct(kpis.penetration) : '—'}</div>
          <div className="kpi-delta neutral">football users ÷ all mobile users</div>
        </div>
      </div>

      {loading && <div className="loading-state"><div className="spinner" /> Fetching data…</div>}
      {error && !loading && (
        <div className="empty-state" style={{ color: 'var(--negative)' }}>Error: {error}</div>
      )}

      {!loading && !error && (
        <>
          {/* ── Market-share chart ─────────────────────────────────── */}
          {chart && chart.categories.length > 0 && (
            <div className="dj-section" style={{ marginBottom: 20 }}>
              <div className="dj-section-header">
                <span className="dj-section-dot" style={{ background: COLORS.teal }} />
                <h2 className="dj-section-title">
                  App market share among <strong>{chart.title}</strong> fans
                  {chart.isSub && <span className="fa-group-tag">{chart.group}</span>}
                  {metric === 'pct' ? ' — % of row total' : metric === 'index' ? ' — index vs national' : ' — users'}
                </h2>
              </div>
              <BarChart
                categories={chart.categories}
                series={[{ name: chart.title, data: chart.values, color: COLORS.teal }]}
                height={Math.max(180, chart.categories.length * 30 + 56)}
                horizontal
                markLines={metric === 'index' ? [100] : []}
                xFormatter={v => (metric === 'pct' ? `${v.toFixed(1)}%` : metric === 'index' ? Math.round(v) : fmtM(v))}
                tooltipFormatter={v => (metric === 'pct' ? `${(+v).toFixed(2)}%` : metric === 'index' ? Math.round(v) : fmtM(v))}
              />
            </div>
          )}

          {/* ── Pivot table ────────────────────────────────────────── */}
          <div className="dj-section fa-pivot-wrap">
            <div className="dj-section-header">
              <span className="dj-section-dot" style={{ background: COLORS.blue }} />
              <h2 className="dj-section-title">
                Football × App overlap — {metric === 'pct' ? '% of row total' : metric === 'index' ? 'index vs national (100 = avg)' : 'active users (xy)'}
              </h2>
              <input
                className="filter-select fa-entity-search"
                placeholder="Search football entity…"
                value={entitySearch}
                onChange={e => setEntitySearch(e.target.value)}
              />
            </div>

            {columns.length === 0 ? (
              <div className="empty-state">Select a micro-category or tick apps to build the pivot.</div>
            ) : (
              <div className="fa-pivot-scroll">
                <table className="data-table fa-pivot">
                  <thead>
                    <tr>
                      <th className="fa-sticky-col">Football entity</th>
                      {columns.map(app => (
                        <th
                          key={app}
                          className={`fa-num fa-sortable ${sort.app === app ? 'fa-sorted' : ''}`}
                          onClick={() => onSortClick(app)}
                          title="Click to sort"
                        >
                          {app}{sort.app === app ? (sort.dir === 'desc' ? ' ▼' : ' ▲') : ''}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleGroups.length === 0 && (
                      <tr>
                        <td className="fa-sticky-col" colSpan={columns.length + 1}
                            style={{ color: 'var(--text-muted)' }}>
                          No football entity matches “{entitySearch}”.
                        </td>
                      </tr>
                    )}
                    {visibleGroups.map(g => {
                      const major = g.groupRow;
                      if (!major) return null;
                      const isOpen     = g.forceOpen || expanded.has(g.group);
                      const hasBrands  = g.brands.length > 0;
                      return (
                        <Fragment key={g.group}>
                          <tr
                            className={
                              'fa-group-row ' +
                              (major.football_y === activeFootball ? 'fa-active-row' : '')
                            }
                            onClick={() => {
                              setActiveFootball(major.football_y);
                              if (hasBrands) toggleExpand(g.group);
                            }}
                          >
                            <td className="fa-sticky-col">
                              <span className="fa-caret">
                                {hasBrands ? (isOpen ? '▾' : '▸') : ''}
                              </span>
                              {major.football_y}
                              {hasBrands && (
                                <span className="fa-group-tag">{g.brands.length}</span>
                              )}
                            </td>
                            {columns.map(app => (
                              <td key={app} className="fa-num" style={cellStyle(major, app)}>{fmtCell(major, app)}</td>
                            ))}
                          </tr>
                          {isOpen && g.brands.map(row => (
                            <tr
                              key={row.football_y}
                              className={
                                'fa-sub-row ' +
                                (row.football_y === activeFootball ? 'fa-active-row' : '')
                              }
                              onClick={() => setActiveFootball(row.football_y)}
                            >
                              <td className="fa-sticky-col fa-sub-cell">{row.football_y}</td>
                              {columns.map(app => (
                                <td key={app} className="fa-num" style={cellStyle(row, app)}>{fmtCell(row, app)}</td>
                              ))}
                            </tr>
                          ))}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Custom: Compare apps across football entities ──────────── */}
          {compareAppList.length > 0 && (
            <div className="dj-section" style={{ marginTop: 20 }}>
              <div className="dj-section-header">
                <span className="dj-section-dot" style={{ background: COLORS.amber }} />
                <h2 className="dj-section-title">
                  Compare apps across football entities {metric === 'pct' ? '(% of row total)' : '(users)'}
                </h2>
              </div>

              <div className="fa-compare-picker">
                {compareAppList.map(app => (
                  <label key={app} className={`fa-chip ${compareApps.includes(app) ? 'active' : ''}`}>
                    <input type="checkbox" checked={compareApps.includes(app)} onChange={() => toggleCompare(app)} />
                    {app}
                  </label>
                ))}
              </div>

              {compareEntities.length > 0 && compareApps.length > 0 && (
                <BarChart
                  categories={compareEntities.map(r => r.football_y)}
                  series={compareApps.map((app, i) => ({
                    name: app,
                    data: compareEntities.map(r => +cmpVal(r, app).toFixed(2)),
                    color: PALETTE[i % PALETTE.length],
                  }))}
                  height={340}
                  tooltipFormatter={v => (metric === 'pct' ? `${(+v).toFixed(2)}%` : fmtM(v))}
                />
              )}

              <div className="fa-pivot-scroll" style={{ marginTop: 12 }}>
                <table className="data-table fa-pivot">
                  <thead>
                    <tr>
                      <th className="fa-sticky-col">Football entity</th>
                      {compareApps.map(a => <th key={a} className="fa-num">{a}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {compareEntities.map(r => (
                      <tr key={r.football_y}>
                        <td className="fa-sticky-col">{r.football_y}</td>
                        {compareApps.map(a => <td key={a} className="fa-num" style={cellStyle(r, a)}>{fmtCell(r, a)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
